import { Box, Card, Flex, Grid, Text } from "@radix-ui/themes";
import DashboardShell from "../../components/dashboard/DashboardShell";
import { BANNER_STYLE, CARD_STYLE, MONO_STYLE, STAT_STYLE } from "../../components/dashboard/tokens";
import UpgradeForm from "../../components/dashboard/UpgradeForm";
import InvoicePortalCard from "../../components/shared/InvoicePortalCard";
import LoadingIndicator from "../../components/shared/LoadingIndicator";
import type { MonthlyUsage } from "../../contexts/UsageContext";
import { useUsage } from "../../contexts/UsageContext";
import { useUsageWarning } from "../../hooks/useUsageWarning";
import { fillMonths, formatResetCountdown, formatResetDate, monthLabel } from "../../utils/usageMonths";

const CHART_HEIGHT = 180;
const CHART_MONTHS = 6;

/**
 * Monthly call volume. usage_counters holds one row per user per month, so the
 * month is the finest grain this data actually has — hence no range/grouping
 * controls.
 */
function MonthlyUsageChart({ history }: { history: MonthlyUsage[] }) {
    const peak = Math.max(1, ...history.map((row) => row.calls));
    // Round the axis up to something readable rather than the raw peak.
    const magnitude = 10 ** Math.max(0, String(Math.round(peak)).length - 1);
    const yMax = Math.max(magnitude, Math.ceil(peak / magnitude) * magnitude);

    return (
        <Flex gap="3">
            <Flex
                direction="column"
                justify="between"
                align="end"
                style={{
                    width: 46,
                    flex: "none",
                    height: CHART_HEIGHT,
                    fontSize: "11px",
                    color: "var(--dc-muted-2)",
                    ...MONO_STYLE,
                }}
            >
                <span>{yMax.toLocaleString()}</span>
                {/* Blank rather than a duplicate tick when the scale is too small to halve. */}
                <span>{yMax >= 2 ? Math.round(yMax / 2).toLocaleString() : ""}</span>
                <span>0</span>
            </Flex>

            <Box
                style={{
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Box
                    style={{
                        position: "relative",
                        height: CHART_HEIGHT,
                    }}
                >
                    <Flex
                        direction="column"
                        justify="between"
                        style={{
                            position: "absolute",
                            inset: 0,
                        }}
                    >
                        <div
                            style={{
                                height: 1,
                                backgroundColor: "var(--dc-card-border)",
                            }}
                        />
                        <div
                            style={{
                                height: 1,
                                backgroundColor: "var(--dc-card-border)",
                            }}
                        />
                        <div
                            style={{
                                height: 1,
                                backgroundColor: "var(--dc-card-border)",
                            }}
                        />
                    </Flex>
                    <Flex
                        align="end"
                        gap="2"
                        style={{
                            position: "relative",
                            height: "100%",
                        }}
                    >
                        {history.map((row) => (
                            <Box
                                key={row.month}
                                style={{
                                    flex: 1,
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "flex-end",
                                }}
                                title={`${monthLabel(row.month)}: ${row.calls.toLocaleString()} calls`}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        height: `${Math.max(row.calls > 0 ? 2 : 0, (row.calls / yMax) * 100)}%`,
                                        backgroundColor: "var(--blue-9)",
                                        borderRadius: "3px 3px 0 0",
                                    }}
                                />
                            </Box>
                        ))}
                    </Flex>
                </Box>
                {/* Labels share the bars' flex sizing so each sits under its own bar. */}
                <Flex gap="2" mt="2">
                    {history.map((row) => (
                        <Box
                            key={row.month}
                            style={{
                                flex: 1,
                                textAlign: "center",
                                fontSize: "11.5px",
                                color: "var(--dc-muted-2)",
                                ...MONO_STYLE,
                            }}
                        >
                            {monthLabel(row.month)}
                        </Box>
                    ))}
                </Flex>
            </Box>
        </Flex>
    );
}

const UsagePage = () => {
    const { usage, monthlyHistory, loading, billingVisible } = useUsage();
    const usageWarning = useUsageWarning();

    if (loading) {
        return (
            <DashboardShell title="Usage">
                <LoadingIndicator text="Loading usage…" />
                <InvoicePortalCard />
            </DashboardShell>
        );
    }

    if (!usage) {
        return (
            <DashboardShell title="Usage">
                <Text as="p" size="2" color="gray">
                    Usage data is unavailable right now. Try again later.
                </Text>
                <InvoicePortalCard />
            </DashboardShell>
        );
    }

    const isFree = usage.plan === "free";
    const chartMonths = fillMonths(monthlyHistory ?? [], CHART_MONTHS);
    const callsLeft = usage.callsIncluded !== null ? Math.max(0, usage.callsIncluded - usage.callsUsed) : null;
    const dailyAverage = Math.round(usage.callsUsed / new Date().getUTCDate());

    return (
        <DashboardShell title="Usage">
            <Flex direction="column" gap="5">
                {/* Hidden while the shell's usage banner shows, so the two don't repeat each other. */}
                {isFree && callsLeft !== null && billingVisible && !usageWarning.visible && (
                    <Box px="4" py="3" style={BANNER_STYLE}>
                        <Flex align="center" justify="between" gap="4" wrap="wrap">
                            <Box
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <Text as="div" size="2" weight="medium">
                                    {callsLeft.toLocaleString()} tool calls left this month, resets on {formatResetDate(usage.resetsAt)}
                                </Text>
                                <Text as="div" size="2" color="gray">
                                    Pro removes the monthly limit — $20 / month, plus VAT and other applicable taxes.
                                </Text>
                            </Box>
                            <UpgradeForm surface="usage_banner" variant="outline" />
                        </Flex>
                    </Box>
                )}

                <Grid
                    columns={{
                        initial: "1",
                        sm: "1.6fr 1fr",
                    }}
                    gap="4"
                >
                    <Card size="3" style={CARD_STYLE}>
                        <Text as="p" size="2" color="gray" mb="2">
                            Tool calls this month
                        </Text>
                        <Flex align="baseline" gap="2" mb="3">
                            <Text size="8" style={STAT_STYLE}>
                                {usage.callsUsed.toLocaleString()}
                            </Text>
                            {/* Three real states, not two: a real limit being enforced
                            ("/100"), genuinely unlimited Pro ("no limit"), and a
                            configured-but-unenforced limit (off/shadow, free user) —
                            that last one gets nothing, not "no limit"; the account
                            isn't unlimited, enforcement is just off right now. */}
                            {usage.callsIncluded !== null && billingVisible && (
                                <Text
                                    size="3"
                                    style={{
                                        color: "var(--dc-muted-2)",
                                    }}
                                >
                                    / {usage.callsIncluded.toLocaleString()}
                                </Text>
                            )}
                            {usage.callsIncluded === null && (
                                <Text
                                    size="3"
                                    style={{
                                        color: "var(--dc-muted-2)",
                                    }}
                                >
                                    no limit
                                </Text>
                            )}
                        </Flex>
                        {usage.callsIncluded !== null && billingVisible && (
                            <>
                                <Box
                                    style={{
                                        height: 7,
                                        borderRadius: 4,
                                        backgroundColor: "var(--dc-raise-bg)",
                                        overflow: "hidden",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: `${Math.min(100, usage.pct ?? 0)}%`,
                                            height: "100%",
                                            backgroundColor: "var(--blue-9)",
                                        }}
                                    />
                                </Box>
                                <Text
                                    as="p"
                                    size="2"
                                    mt="2"
                                    style={{
                                        color: "var(--dc-muted-2)",
                                    }}
                                >
                                    {Math.min(100, Math.round(usage.pct ?? 0))}% used · Resets on {formatResetDate(usage.resetsAt)},{" "}
                                    {formatResetCountdown(usage.resetsAt)}
                                </Text>
                            </>
                        )}
                    </Card>

                    <Card size="3" style={CARD_STYLE}>
                        <Text as="p" size="2" color="gray" mb="2">
                            Daily average
                        </Text>
                        <Text as="div" size="7" style={STAT_STYLE}>
                            {dailyAverage.toLocaleString()}
                        </Text>
                        <Text
                            as="p"
                            size="2"
                            mt="2"
                            style={{
                                color: "var(--dc-muted-2)",
                            }}
                        >
                            Calls so far this month ÷ days elapsed.
                        </Text>
                    </Card>
                </Grid>

                <Card size="3" style={CARD_STYLE}>
                    <Box mb="4">
                        <Text
                            as="div"
                            size="3"
                            weight="medium"
                            style={{
                                fontFamily: "var(--dc-font-heading)",
                            }}
                        >
                            Calls over time
                        </Text>
                        <Text as="div" size="2" color="gray">
                            Last {CHART_MONTHS} months, grouped by month
                        </Text>
                    </Box>
                    <MonthlyUsageChart history={chartMonths} />
                </Card>
            </Flex>
            <InvoicePortalCard />
        </DashboardShell>
    );
};

export default UsagePage;
